import { Checkbox, Divider, Grid, MenuItem, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import {
  AvailableIdentityProviders,
  NewCustomer,
} from '../Model/CustomerModel';
import { ValidationErrors, Validator } from 'fluentvalidation-ts';
import { customerService } from '../CustomerService';
import { ValidateEmails } from '../../EmailAdministration/Common/Common';
import { CardWrapper } from '../../Common/CommonComponents';
import {
  CustomSelect,
  CustomTextField,
} from '../../../../Common/Components/Controls/InputControls';

interface TenantFormProps {
  customer: NewCustomer;
  handleFieldValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  groupNames: string[];
  errors: ValidationErrors<NewCustomer>;
  setErrors: React.Dispatch<
    React.SetStateAction<ValidationErrors<NewCustomer>>
  >;
  enableExternalIdentity: boolean;
  SetEnableExternalIdentity: React.Dispatch<React.SetStateAction<boolean>>;
  SetEnableProArcLogin: (value: boolean) => void;
}

class CustomerValidator extends Validator<NewCustomer> {
  constructor() {
    super();
    this.ruleFor('Subdomain')
      .notEmpty()
      .withMessage('Subdomain cannot be empty')
      .notNull()
      .withMessage('Subdomain cannot be empty');

    this.ruleFor('CustomerName')
      .notEmpty()
      .withMessage('Customer Name cannot be empty')
      .notNull()
      .withMessage('Customer Name cannot be empty');

    this.ruleFor('GroupName')
      .notEmpty()
      .withMessage('Group Name cannot be empty')
      .notNull()
      .withMessage('Group Name cannot be empty');

    this.ruleFor('AdminUser')
      .notEmpty()
      .withMessage('Admin User cannot be empty')
      .emailAddress()
      .withMessage('Should be email address')
      .notNull()
      .withMessage('Admin User Name cannot be empty');

    this.ruleFor('AzureADAppClientID')
      .notEmpty()
      .withMessage('Azure AD App Client ID cannot be empty')
      .notNull()
      .withMessage('Azure AD App Client ID cannot be empty');

    this.ruleFor('AuthProviderName')
      .notEmpty()
      .withMessage('Auth Provider Name cannot be empty')
      .notNull()
      .withMessage('Auth Provider Name cannot be empty');

    this.ruleFor('WSFederationUrl')
      .notEmpty()
      .withMessage('Auth Metadata URL cannot be empty')
      .notNull()
      .withMessage('Auth Metadata URL cannot be empty');

    this.ruleFor('InputClaimType')
      .notEmpty()
      .withMessage('Input Claim Type cannot be empty')
      .notNull()
      .withMessage('Input Claim Type cannot be empty');

    this.ruleFor('ContactPersons')
      .notEmpty()
      .withMessage('Contact Email cannot be empty')
      .must((value: unknown) => ValidateEmails(value))
      .withMessage('Each email should be a valid email address');

    this.ruleFor('AdminUserName')
      .notEmpty()
      .withMessage('Contact Person cannot be empty')
      .notNull()
      .withMessage('Admin User Name cannot be empty');
  }
}

async function checkSubdomainExists(subdomain: string) {
  return customerService.validateSubdomain(subdomain);
}

const CloneTenantForm = (props: TenantFormProps) => {
  const {
    customer,
    handleFieldValueChange,
    groupNames,
    errors,
    setErrors,
    enableExternalIdentity,
    SetEnableExternalIdentity,
    SetEnableProArcLogin,
  } = props;

  const customerValidator = new CustomerValidator();

  const validate = () => {
    const customerErrors = customerValidator.validate(customer);
    setErrors(customerErrors);
  };

  const handleSubdomainExists = () => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      Subdomain: 'This subdomain already exists',
    }));
  };

  const handleSubdomainNotExists = () => {
    setErrors((prevErrors) => ({ ...prevErrors, Subdomain: '' }));
  };

  const validateSubdomain = () => {
    if (customer.Subdomain) {
      checkSubdomainExists(customer.Subdomain).then((exists) => {
        if (exists) {
          handleSubdomainExists();
        }
        if (!exists) {
          handleSubdomainNotExists();
        }
      });
    }
    validate();
  };

  useEffect(() => {
    validateSubdomain();
  }, [customer.Subdomain]);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <CardWrapper>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Basic Informations
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Subdomain</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  error={!!errors.Subdomain}
                  helperText={errors.Subdomain}
                  value={customer.Subdomain}
                  placeholder="Subdomain"
                  name="Subdomain"
                  onChange={handleFieldValueChange}
                  onBlur={validateSubdomain}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Customer Name</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  error={!!errors.CustomerName}
                  helperText={errors.CustomerName}
                  value={customer.CustomerName}
                  placeholder="Customer Name"
                  name="CustomerName"
                  onChange={handleFieldValueChange}
                  onBlur={validate}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Enterprise Number</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  value={customer.EnterpriseNumber}
                  placeholder="Enterprise Number"
                  name="EnterpriseNumber"
                  onChange={handleFieldValueChange}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Installation type</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField disabled value={customer.SetupType} />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Contact Email</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  error={!!errors.ContactPersons}
                  helperText={errors.ContactPersons}
                  value={customer.ContactPersons}
                  placeholder="Contact Email"
                  name="ContactPersons"
                  onChange={handleFieldValueChange}
                  onBlur={validate}
                />
              </Grid>
            </Grid>
          </CardWrapper>
          <CardWrapper sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Group Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Group Name</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomSelect
                  error={!!errors.GroupName}
                  helperText={errors.GroupName}
                  value={customer.GroupName ?? ''}
                  placeholder="Group Name"
                  name="GroupName"
                  onChange={handleFieldValueChange}
                  onBlur={validate}
                >
                  {groupNames.map((group) => {
                    return (
                      <MenuItem key={group} value={group}>
                        {group}
                      </MenuItem>
                    );
                  })}
                </CustomSelect>
              </Grid>
            </Grid>
          </CardWrapper>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <CardWrapper>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Authenticatin
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Enable Proarc Login</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Checkbox
                  value={customer.EnableProArcLogin}
                  checked={customer.EnableProArcLogin}
                  name="EnableProArcLogin"
                  onChange={(e) => SetEnableProArcLogin(e.target.checked)}
                  onBlur={validate}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">
                  Enable External Identity Provider
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Checkbox
                  value={enableExternalIdentity ?? false}
                  checked={enableExternalIdentity ?? false}
                  onChange={(e) => SetEnableExternalIdentity(e.target.checked)}
                  onBlur={validate}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Admin Email Id</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  error={!!errors.AdminUser}
                  helperText={errors.AdminUser}
                  value={customer.AdminUser}
                  placeholder="Admin User"
                  name="AdminUser"
                  onChange={handleFieldValueChange}
                  onBlur={validate}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Admin User Name</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  error={!!errors.AdminUserName}
                  helperText={errors.AdminUserName}
                  value={customer.AdminUserName}
                  placeholder="Admin User Name"
                  name="AdminUserName"
                  onChange={handleFieldValueChange}
                  onBlur={validate}
                />
              </Grid>
            </Grid>
            {enableExternalIdentity && (
              <>
                <Grid container spacing={2} alignItems="center" mt={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">Identity Provider</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomSelect
                      error={!!errors.IdentityProviders}
                      helperText={errors.IdentityProviders}
                      value={customer.IdentityProviders ?? ''}
                      placeholder="Identity Provider"
                      name="IdentityProviders"
                      onChange={handleFieldValueChange}
                      onBlur={validate}
                    >
                      {Object.entries(AvailableIdentityProviders).map(
                        ([key, value]) => {
                          return (
                            <MenuItem key={key} value={value}>
                              {value}
                            </MenuItem>
                          );
                        },
                      )}
                    </CustomSelect>
                  </Grid>
                </Grid>
                <Grid container spacing={2} alignItems="center" mt={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">Input Claim Type</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      error={!!errors.InputClaimType}
                      helperText={errors.InputClaimType}
                      value={customer.InputClaimType}
                      placeholder="Input Claim Type"
                      name="InputClaimType"
                      onChange={handleFieldValueChange}
                      onBlur={validate}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} alignItems="center" mt={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">Auth Client ID</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      error={!!errors.AzureADAppClientID}
                      helperText={errors.AzureADAppClientID}
                      value={customer.AzureADAppClientID}
                      placeholder="Auth Client ID"
                      name="AzureADAppClientID"
                      onChange={handleFieldValueChange}
                      onBlur={validate}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} alignItems="center" mt={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">Auth Metadata URL</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      error={!!errors.WSFederationUrl}
                      helperText={errors.WSFederationUrl}
                      value={customer.WSFederationUrl}
                      placeholder="Auth Metadata URL"
                      name="WSFederationUrl"
                      onChange={handleFieldValueChange}
                      onBlur={validate}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} alignItems="center" mt={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">Auth Provider Name</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      error={!!errors.AuthProviderName}
                      helperText={errors.AuthProviderName}
                      value={customer.AuthProviderName}
                      placeholder="Auth Provider Name"
                      name="AuthProviderName"
                      onChange={handleFieldValueChange}
                      onBlur={validate}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </CardWrapper>
        </Grid>
      </Grid>
    </>
  );
};

export default CloneTenantForm;
